import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box3, MOUSE, OrthographicCamera, PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { isDefined } from '../../../utils/variables';
import { GeodataSpatialReferenceContext } from '../../components/cloud-processing/gcp-page/geodata-spatial-reference-state';
import { centerCameraOnBoundingBox } from '../../components/cloud-processing/gcp-page/use-center-camera-callback';

/**
 * Sets up orbit controls for the referenced camera using ThreeJS build-in functionality.
 * When the camera changes the old controls will automatically be disposed and new controls are setup,
 * and the camera transformation will be preserved as good as possible.
 *
 * If it's an OrthoGraphic camera the rotation will automatically be locked.
 * If center is defined it will focus on that point initially.
 *
 * TODO: Pass an object array that can be used with a raycaster to update the target point more
 * like it is done in SkyView today.
 */
export function useOrbitControls(
  ref?: React.RefObject<HTMLElement>,
  camera?: PerspectiveCamera | OrthographicCamera,
  customBoundingBox?: Box3 | null,
) {
  const context = useContext(GeodataSpatialReferenceContext);
  const boundingBox = useMemo(() => {
    return context ? context.boundingBox : customBoundingBox;
  }, [context, customBoundingBox]);
  const [controls, setControls] = useState<OrbitControls>();
  const controlsRef = useRef<OrbitControls | null>(null);
  const didInitialCenterRef = useRef(false);

  useEffect(() => {
    // Wait for all dependencies to be ready before initializing
    // This ensures cameraCenter is fully loaded before creating controls
    if (!isDefined(camera) || !isDefined(ref?.current)) {
      return;
    }

    const domElement = ref.current;

    // Check if controls already exist for this exact camera/center combination
    // This prevents duplicate creation from React StrictMode
    if (controlsRef.current && controlsRef.current.object === camera) {
      return;
    }

    // Dispose any existing controls BEFORE creating new ones
    // This prevents multiple controls from being attached to the same DOM element
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }

    setControls((oldValue) => {
      // If controls already exist in ref and match, reuse them
      if (controlsRef.current && controlsRef.current.object === camera) {
        return controlsRef.current;
      }

      // Dispose old controls immediately if they exist
      if (oldValue && oldValue !== controlsRef.current) {
        oldValue.dispose();
      }

      const newControls = new OrbitControls(camera, domElement);

      // Store in ref immediately to prevent duplicates
      controlsRef.current = newControls;

      let zoom = 1;
      let fov = 75;
      let aspect = domElement.clientWidth / domElement.clientHeight;
      const cameraOffset = new Vector3(0, 0, 100);

      // If old controls are defined, copy the values
      if (isDefined(oldValue) && oldValue !== newControls) {
        newControls.target.copy(oldValue.target);
        cameraOffset.setZ(oldValue.getDistance());

        if (oldValue.object instanceof PerspectiveCamera) {
          fov = oldValue.object.fov;
          aspect = oldValue.object.aspect;
        } else if (oldValue.object instanceof OrthographicCamera) {
          zoom = oldValue.object.zoom;
        }
      }

      const distance = cameraOffset.z;

      // Orthographic camera should always be far away from the target for everything to show on screen.
      if (camera instanceof OrthographicCamera) {
        cameraOffset.setZ(5000);
      }

      camera.position.copy(newControls.target.clone().add(cameraOffset));

      // Ensure mouseButtons are set again after all configuration
      // This provides an extra safeguard
      newControls.mouseButtons = {
        LEFT: MOUSE.PAN,
        MIDDLE: MOUSE.DOLLY,
        RIGHT: MOUSE.ROTATE,
      };

      newControls.screenSpacePanning = true;
      newControls.rotateSpeed = 1;
      newControls.zoomSpeed = 8;
      newControls.minDistance = 0.1;
      newControls.maxDistance = 5000;
      newControls.zoomToCursor = true;

      const currentCamera = camera; // camera is guaranteed to be defined
      if (currentCamera instanceof OrthographicCamera) {
        newControls.enableRotate = false;
        currentCamera.zoom = zoom;

        const fovRadians = fov * (Math.PI / 180);
        const height = 2 * distance * Math.tan(fovRadians / 2);
        const width = height * aspect;

        currentCamera.left = -width / 2;
        currentCamera.right = width / 2;
        currentCamera.top = height / 2;
        currentCamera.bottom = -height / 2;
      }

      currentCamera.near = 0.1;
      currentCamera.far = 10000;
      currentCamera.updateProjectionMatrix();

      newControls.update();

      return newControls;
    });

    // Cleanup function for React StrictMode unmounting
    // This ensures controls are disposed when dependencies change or component unmounts
    return () => {
      // Dispose controls immediately via ref (synchronous)
      if (controlsRef.current) {
        controlsRef.current.dispose();
        controlsRef.current = null;
      }

      // Also dispose via state (asynchronous, but ensures cleanup)
      setControls((currentControls) => {
        if (currentControls && currentControls !== controlsRef.current) {
          currentControls.dispose();
        }
        return undefined;
      });
    };
  }, [ref, camera]);

  // Only center the camera once after the initial render.
  useEffect(() => {
    if (
      didInitialCenterRef.current ||
      !isDefined(controls) ||
      !isDefined(camera) ||
      !isDefined(boundingBox)
    ) {
      return;
    }

    didInitialCenterRef.current = true;
    centerCameraOnBoundingBox(controls, camera, boundingBox);
  }, [controls, camera, boundingBox]);

  return controls;
}
